import musicController from "../audio/music-controller";
import { PORTAL } from "./portal-env";

// The game sites' SDKs (shared/portal-env.ts says which site a build is for).
// CrazyGames: docs.crazygames.com/sdk (HTML5, v3).

type Params = Record<string, string>;
interface CgSdk {
  init(): Promise<void>;
  environment: "local" | "crazygames" | "disabled";
  game: {
    gameplayStart(): void;
    gameplayStop(): void;
    loadingStart(): void;
    loadingStop(): void;
    settings: { muteAudio?: boolean };
    addSettingsChangeListener(fn: (s: { muteAudio?: boolean }) => void): void;
    // playing with friends (docs.crazygames.com/requirements/multiplayer)
    isInstantMultiplayer?: boolean;
    inviteParams?: Params | null;
    inviteLink(params: Params): string;
    updateRoom(room: { roomId: string; isJoinable: boolean; inviteParams?: Params }): void;
    leftRoom(): void;
    addJoinRoomListener(fn: (params: Params) => void): void;
  };
}
// (on another domain the SDK is "disabled" and every call throws)
const cg = (): CgSdk | null => {
  const sdk = (typeof window !== "undefined" && (window as any).CrazyGames?.SDK) as CgSdk | undefined;
  return sdk && sdk.environment !== "disabled" ? sdk : null;
};
const safe = (fn: () => void) => { try { fn(); } catch { /* the SDK refused: the game goes on */ } };

// Before the app renders: the SDK is up (or given up on after a few seconds:
// the game starts anyway), the loading is reported, its mute followed, and
// every stretch of play (the game's music on and off: a match's rounds, or the
// match over) reported as gameplay.
export async function startPortal(): Promise<void> {
  if (PORTAL !== "crazygames") return;
  const sdk = (window as any).CrazyGames?.SDK as CgSdk | undefined;
  if (!sdk) return;
  await Promise.race([sdk.init().catch(() => {}), new Promise((res) => window.setTimeout(res, 4000))]);
  const live = cg();
  if (!live) return;
  safe(() => live.game.loadingStart());
  const apply = (st: { muteAudio?: boolean }) => musicController.setExternalMute(!!st.muteAudio);
  safe(() => apply(live.game.settings || {}));
  safe(() => live.game.addSettingsChangeListener(apply));
  musicController.onPlayingChange((on) => safe(() => (on ? live.game.gameplayStart() : live.game.gameplayStop())));

  // Playing with friends: our game rooms are their rooms. Opened from a
  // friend's invite, the game starts in that room; from their "play with
  // friends" button (instant multiplayer), in a new private room of your own;
  // an invite taken while the game runs moves you into that room. (The pages
  // are routed in the #: setting it before the app renders is where it starts.)
  const invited = roomCode(live.game.inviteParams?.room);
  if (invited) window.location.hash = "#/lobby/" + invited;
  else if (live.game.isInstantMultiplayer) window.location.hash = "#/join?create=private";
  safe(() => live.game.addJoinRoomListener((params) => {
    const code = roomCode(params && params.room);
    if (code) window.location.hash = "#/lobby/" + code;
  }));
}

// A room code as the rooms take it (the Join page's rule).
const roomCode = (c: unknown): string | null => {
  const code = typeof c === "string" ? c.trim().toUpperCase().slice(0, 8) : "";
  return /^[A-Z0-9]{3,8}$/.test(code) ? code : null;
};

// The game room you are in (null: none), and whether friends can join it now
// (not while its match is on): the site shows it to your friends.
export function portalRoom(code: string | null, joinable: boolean): void {
  const live = cg();
  if (!live) return;
  if (code) safe(() => live.game.updateRoom({ roomId: code, isJoinable: joinable, inviteParams: { room: code } }));
  else safe(() => live.game.leftRoom());
}

// The site's own link into a room (null when the site has none).
export function portalInviteLink(code: string): string | null {
  const live = cg();
  if (!live) return null;
  try { return live.game.inviteLink({ room: code }) || null; } catch { return null; }
}

// The first screen is up: the loading is over.
export function portalLoaded(): void {
  const live = cg();
  if (live) safe(() => live.game.loadingStop());
}
